import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer } from 'recharts';

import { ReactComponent as PinActiveIcon } from '../../../../assets/icons/pinActiveIcon.svg';
import { ReactComponent as PinIcon } from '../../../../assets/icons/PinIcon.svg';
import { currentBranding } from '../../../../config/branding';
import { branding, chartTimePeriod } from '../../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { clearActivePin, setActivePin } from '../../../../redux/slice/ReportSlice';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import report from '../../../../utils/api/report';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

/**
 * Energy report trend chart
 * This component displays the energy consumption trend over time.
 */
function RevenueReportTrendChart() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('reports');
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const reportsData = useAppSelector((state) => state.reportData);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#00A79D',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        displayColors: false,
        callbacks: {
          /**
           * Returns the tooltip title.
           * @returns The tooltip title string.
           */
          title: () => t('totalRevenue'),
          /**
           * Formats the tooltip label using locale string.
           * @param context - The tooltip context object.
           * @returns The formatted label string.
           */
          label: (context: any) =>
            `${context.raw.toLocaleString()} ${currentBranding === branding.ENERGIC ? 'SAR' : 'USD'}`,
        },
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
      },
      y: {
        grid: { display: false },
        ticks: { display: false },
        border: { display: false },
      },
    },
  };

  /**
   * Fetches the power consumed report data for the specified time range and updates the chart data.
   * @param range - The time range for the report ('last24hours', 'last7days', 'lastMonth', 'last12months').
   */
  const fetchtotalRevenueReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    try {
      const response = await report.fetchReportchart({
        timePeriod: range,
        reportType: 'totalRevenue',
      });

      if (response.status === 200) {
        const { data } = response;
        switch (range) {
          case 'last12months':
            setBarGraphData(fillMissingMonthsGeneric(data));
            break;
          case 'lastMonth':
            setBarGraphData(fillMissingLastNDaysGeneric(data, 30));
            break;
          case 'last7days':
            setBarGraphData(fillMissingLastNDaysGeneric(data, 7));
            break;
          case 'last24hours':
            setBarGraphData(fillMissingLast24HrsGeneric(data));
            break;
          default:
            setBarGraphData([]);
            break;
        }
      }
    } catch (error) {
      console.error(t('failedToFetchEnergyReportData'), error);
    }
  };
  /**
   * If clicking the already active pin, deactivate it
   */
  const handleClick = (name: string) => {
    if (reportsData.activePinName.includes(name)) {
      dispatch(clearActivePin(name));
    } else {
      dispatch(setActivePin(name));
    }
  };

  useEffect(() => {
    fetchtotalRevenueReport('last24hours');
  }, []);

  const labels = barGraphData.map((d) => d.month || d.label);
  const values = barGraphData.map((d) => d.totalRevenue || 0);

  return (
    <div className="energy-report-chart">
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">
            <button
              type="button"
              aria-label="Pin energy consumption trend based on revenue tiers"
              className={`pin-button ${reportsData.activePinName.includes('totalRevenue') ? 'active' : ''}`}
              onClick={() => handleClick('totalRevenue')}>
              {reportsData.activePinName.includes('totalRevenue') ? (
                <span className="pin-icon">
                  <PinActiveIcon />
                </span>
              ) : (
                <span className="pin-icon-wrapper">
                  <span className="pin-icon default">
                    <PinIcon />
                  </span>
                </span>
              )}
            </button>
            {t('totalRevenueTrend')}
          </div>
          <CustomToggleButton
            labels={chartTimePeriod.map((item) => t(item.label))}
            onToggle={(index) => {
              const selected = chartTimePeriod[index];
              if (selected) fetchtotalRevenueReport(selected.value);
            }}
            defaultActiveIndex={0}
            type="chart"
          />
        </div>

        <div className="energy-report-container">
          <ResponsiveContainer width="100%" height={250}>
            <Line
              data={{
                labels,
                datasets: [
                  {
                    label: t('powerConsumed'),
                    data: values,
                    borderColor: '#00A79D',
                    backgroundColor: '#00A79D',
                    fill: false,
                    tension: 0.4,
                    borderWidth: 2,
                    pointRadius: 6,
                    pointHoverRadius: 8,
                    pointBackgroundColor: '#00A79D',
                    pointBorderColor: '#00A79D',
                  },
                ],
              }}
              options={chartOptions}
            />
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default RevenueReportTrendChart;
